export const MF_BUY_TYPES = [
  "BUY",
  "DIVIDEND_PAYOUT",
  "DIVIDEND_REINVEST",
  "TRANSFERRED_IN",
  "SWITCH_IN",
  "BONUS",
  "DEMERGER",
  "IPO",
  "MERGER",
  "SPLIT",
] as const;

export const MF_SELL_TYPES = ["SELL", "REDEMPTION", "SWITCH_OUT"] as const;

export const MF_ADD_TX_OPTIONS = [
  { value: "BUY", label: "Buy" },
  { value: "DIVIDEND_PAYOUT", label: "Dividend Payout" },
  { value: "DIVIDEND_REINVEST", label: "Dividend Reinvest" },
  { value: "TRANSFERRED_IN", label: "Transferred In" },
  { value: "SWITCH_IN", label: "Switch In" },
] as const;

export const MF_SELL_TX_OPTIONS = [
  { value: "SELL", label: "Sell" },
  { value: "SWITCH_OUT", label: "Switch Out" },
] as const;

export const MF_TX_OPTIONS = [
  ...MF_ADD_TX_OPTIONS,
  ...MF_SELL_TX_OPTIONS,
] as const;

export const mfTxKey = (type?: string | null) =>
  String(type ?? "")
    .trim()
    .replace(/\s+/g, "_")
    .toUpperCase();

export const isMfBuyType = (type?: string | null): boolean =>
  (MF_BUY_TYPES as readonly string[]).includes(mfTxKey(type));

/** Normalizes selection to API `type` (e.g. BUY, DIVIDEND_PAYOUT). */
export const resolveMfPayloadType = (
  mfTransactionType?: string | null
): string => {
  const key = mfTxKey(mfTransactionType);
  if (!key || key === "PURCHASE" || key === "SWITCH_IN") return "BUY";
  if (key === "REDEMPTION" || key === "SWITCH_OUT") return "SELL";
  return key;
};

export const isMfSellTx = (type?: string | null): boolean => {
  const k = mfTxKey(type);
  return (MF_SELL_TYPES as readonly string[]).includes(k);
};

export const isMfBuyTx = (type?: string | null): boolean => {
  const k = mfTxKey(type);
  return k !== "" && !isMfSellTx(type);
};

const MF_TX_FULL_LABELS: Record<string, string> = {
  BUY: "Buy",
  PURCHASE: "Buy",
  SELL: "Sell",
  REDEMPTION: "Sell",
  DIVIDEND_PAYOUT: "Dividend Payout",
  DIVIDEND_REINVEST: "Dividend Reinvest",
  TRANSFERRED_IN: "Transferred In",
  TRANSFERRED_OUT: "Transferred Out",
  SWITCH_IN: "Switch In",
  SWITCH_OUT: "Switch Out",
  BONUS: "Bonus",
  DEMERGER: "DeMerger",
  IPO: "IPO",
  MERGER: "Merger",
  SPLIT: "Split",
};

/** Short labels for table cells; hover shows full name via mfTxFullLabel. */
const MF_TX_SHORT_LABELS: Record<string, string> = {
  TRANSFERRED_IN: "TFR-IN",
  TRANSFERRED_OUT: "TFR-OUT",
  SWITCH_IN: "SW-IN",
  SWITCH_OUT: "SW-OUT",
  DIVIDEND_PAYOUT: "DIV P/O",
  DIVIDEND_REINVEST: "DIV RNV",
  REDEMPTION: "SELL",
  DEMERGER: "DEMRG",
};

const titleCaseFromKey = (key: string): string =>
  key
    .split("_")
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");

/** Full display name (tooltip / forms). */
export const mfTxFullLabel = (type?: string | null): string => {
  const key = mfTxKey(type);
  if (!key) return "-";
  return MF_TX_FULL_LABELS[key] ?? titleCaseFromKey(key);
};

/** Abbreviated label for narrow table columns. */
export const mfTxLabel = (type?: string | null): string => {
  const key = mfTxKey(type);
  if (!key) return "-";
  return MF_TX_SHORT_LABELS[key] ?? mfTxFullLabel(type);
};
